import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, Send, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "+55 31 98855-7694";
const PHONE_RAW = "+5531988557694";
const EMAIL = "samuelmorais@gmail.com";
const WHATSAPP_LINK = `https://wa.me/${PHONE_RAW.replace("+", "")}?text=${encodeURIComponent(
  "Hi AnimaSoftware, I'd like to discuss a 3D project."
)}`;
const EMAIL_LINK = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
  EMAIL
)}&su=${encodeURIComponent("Contato pelo site AnimaSoftware")}&body=${encodeURIComponent(
  "Olá, gostaria de falar sobre um projeto."
)}`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.project_description) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      console.log("[contact] saved", res.data);
      toast.success("Brief received. We'll be in touch within one business day.");
      setForm({ name: "", email: "", project_description: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8"
    >
      <div className="absolute -top-32 right-0 h-[500px] w-[500px] rounded-full bg-[#FF6B00]/15 blur-[140px]" />
      <div className="absolute -bottom-32 left-0 h-[500px] w-[500px] rounded-full bg-[#00FF66]/10 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00]">
              Let's build
            </div>
            <h2
              className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter"
              data-testid="contact-headline"
            >
              Start your <span className="text-gradient">3D project.</span>
            </h2>
            <p className="mt-6 text-gray-400 text-base leading-relaxed max-w-md">
              Tell us a bit about your vision. Whether it's an MVP or a complex
              real-time platform, we'll come back with a clear plan and a
              proposal.
            </p>

            <div className="mt-10 space-y-3">
              <a
                href={`tel:${PHONE_RAW}`}
                data-testid="contact-phone-button"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#FF6B00]/40 hover:bg-white/[0.06] transition-all"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6B00]/15 text-[#FF6B00]">
                  <Phone className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-400">
                    Call us
                  </div>
                  <div className="font-medium">{PHONE_DISPLAY}</div>
                </div>
              </a>

              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#00FF66]/40 hover:bg-white/[0.06] transition-all"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#00FF66]/15 text-[#00FF66]">
                  <MessageCircle className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-400">
                    WhatsApp
                  </div>
                  <div className="font-medium">Chat with our team</div>
                </div>
              </a>

              <a
                href={EMAIL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-email-button"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#FF6B00]/40 hover:bg-white/[0.06] transition-all"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF6B00] to-[#00FF66] text-black">
                  <Mail className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-400">
                    Email
                  </div>
                  <div className="font-medium">{EMAIL}</div>
                </div>
              </a>
            </div>

            <div className="mt-8">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 max-w-sm">
                <MapPin className="h-4 w-4 text-[#FF6B00] mb-2" />
                <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                  Studio
                </div>
                <div className="text-sm mt-1">Belo Horizonte, MG, Brazil</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="relative rounded-3xl border border-white/10 bg-[#101010]/90 backdrop-blur-xl p-7 sm:p-10"
            >
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-[#FF6B00]/20 via-transparent to-[#00FF66]/20 -z-10 blur-md" />

              <h3 className="font-[Unbounded] text-2xl font-semibold">
                Project brief
              </h3>
              <p className="text-sm text-gray-400 mt-2">
                We typically respond within one business day.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-xs uppercase tracking-[0.18em] text-gray-400"
                  >
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Your full name"
                    required
                    className="mt-2 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-[#FF6B00] focus-visible:border-[#FF6B00]"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="email"
                    className="text-xs uppercase tracking-[0.18em] text-gray-400"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@company.com"
                    required
                    className="mt-2 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-[#FF6B00] focus-visible:border-[#FF6B00]"
                  />
                </div>
              </div>

              <div className="mt-5">
                <Label
                  htmlFor="project_description"
                  className="text-xs uppercase tracking-[0.18em] text-gray-400"
                >
                  Project description
                </Label>
                <Textarea
                  id="project_description"
                  data-testid="contact-input-description"
                  value={form.project_description}
                  onChange={onChange("project_description")}
                  rows={6}
                  placeholder="Tell us the goal, target platform and any constraints we should know about..."
                  required
                  className="mt-2 bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-[#FF6B00] focus-visible:border-[#FF6B00] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-form-submit"
                className="mt-8 group inline-flex items-center justify-center gap-2 px-7 h-13 py-4 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#00FF66] text-black font-semibold text-sm shadow-[0_0_40px_-10px_rgba(255,107,0,0.6)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send brief
                    <Send className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
